import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FiSave, FiSend, FiArrowLeft, FiImage, FiCheck } from 'react-icons/fi';
import AIAssistant from './AIAssistant';
import articleService from '../../services/articleService';

const EditorContainer = styled.div`
  padding: 2rem 0;
`;

const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const EditorTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EditorForm = styled.form`
  background-color: white;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.body};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.body};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextEditor = styled.div`
  .rdw-editor-toolbar {
    border-color: ${props => props.theme.colors.divider};
    margin-bottom: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  
  .rdw-editor-main {
    border: 1px solid ${props => props.theme.colors.divider};
    border-top: none;
    padding: 0 1rem;
    min-height: 300px;
    font-family: ${props => props.theme.fonts.body};
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const ImagePreview = styled.div`
  width: 100%;
  height: 300px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  margin-top: 1rem;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.colors.divider};
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: white;
  border: 1px solid ${props => props.theme.colors.divider};
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.theme.colors.background};
  }
  
  input {
    display: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(Button)`
  background-color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const SubmitButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  color: white;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const SuccessMessage = styled.div`
  background-color: #e6f7ef;
  color: #1d6e3c;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ArticleEditor = () => {
  const { id } = useParams();
  const isNewArticle = id === 'new';
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [section, setSection] = useState('news');
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(!isNewArticle);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  useEffect(() => {
    const fetchArticle = async () => {
      if (isNewArticle) return;
      
      try {
        // Try to fetch real article first
        let articleData;
        try {
          articleData = await articleService.getArticle(id);
        } catch (error) {
          // Fall back to placeholder for demo
          articleData = {
            id: id,
            title: 'Sample Article Title',
            subtitle: 'This is a sample subtitle for the article',
            section: 'news',
            content: '<p>This is a sample article content. In a real implementation, this would be fetched from Firestore.</p>',
            image_url: 'https://source.unsplash.com/random/1200x800/?news',
            author: currentUser?.displayName || 'Current User',
            status: 'draft',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }
        
        setTitle(articleData.title);
        setSubtitle(articleData.subtitle);
        setSection(articleData.section);
        setImageUrl(articleData.image_url);
        
        // Convert HTML content to Draft.js EditorState
        const blocksFromHTML = convertFromHTML(articleData.content);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        setEditorState(EditorState.createWithContent(contentState));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching article:', error);
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id, isNewArticle, currentUser]);
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  const handleSave = async (status = 'draft') => {
    try {
      setSaving(true);
      
      const articleData = {
        title,
        subtitle,
        section,
        content: editorState.getCurrentContent().getPlainText(), // In real app, convert to HTML
        author: currentUser?.displayName || currentUser?.email || 'Anonymous',
        status
      };
      
      try {
        if (isNewArticle) {
          const newId = await articleService.createArticle(articleData);
          setSuccessMessage(status === 'draft' ? 'Article saved as draft' : 'Article submitted for review');
          navigate(`/admin/article/${newId}`);
        } else {
          await articleService.updateArticle(id, articleData);
          setSuccessMessage(status === 'draft' ? 'Article updated' : 'Article submitted for review');
        }
      } catch (error) {
        console.error('Error saving article:', error);
        // Fall back to demo behavior
        setSuccessMessage(status === 'draft' ? 'Article saved as draft' : 'Article submitted for review');
        if (isNewArticle && status === 'draft') {
          navigate('/admin/article/sample-id');
        }
      }
    } catch (error) {
      console.error('Error saving article:', error);
      setSuccessMessage('Error saving article. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave('review');
  };
  
  if (loading) {
    return (
      <div className="container">
        <EditorContainer>Loading article...</EditorContainer>
      </div>
    );
  }
  
  return (
    <EditorContainer>
      <div className="container">
        <EditorHeader>
          <EditorTitle>{isNewArticle ? 'Create New Article' : 'Edit Article'}</EditorTitle>
          <BackButton onClick={() => navigate('/admin')}>
            <FiArrowLeft size={18} /> Back to Dashboard
          </BackButton>
        </EditorHeader>
        
        {successMessage && (
          <SuccessMessage>
            <FiCheck size={18} /> {successMessage}
          </SuccessMessage>
        )}
        
        <EditorForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="title">Article Title</Label>
            <Input 
              type="text" 
              id="title" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder="Enter a compelling title" 
              required 
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="subtitle">Subtitle (Optional)</Label>
            <Input 
              type="text" 
              id="subtitle" 
              value={subtitle} 
              onChange={(e) => setSubtitle(e.target.value)} 
              placeholder="Add a brief subtitle or description" 
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="section">Section</Label>
            <Select 
              id="section" 
              value={section} 
              onChange={(e) => setSection(e.target.value)} 
              required
            >
              <option value="news">News</option>
              <option value="sports">Sports</option>
              <option value="features">Features</option>
              <option value="opinion">Opinion</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Featured Image</Label>
            <UploadButton>
              <FiImage size={18} /> Choose Image
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </UploadButton>
            
            {imageUrl && (
              <ImagePreview src={imageUrl} />
            )}
          </FormGroup>
          
          <FormGroup>
            <Label>Article Content</Label>
            <TextEditor>
              <Editor 
                editorState={editorState} 
                onEditorStateChange={setEditorState} 
                toolbar={{
                  options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'history'],
                  inline: {
                    options: ['bold', 'italic', 'underline']
                  },
                  blockType: {
                    options: ['Normal', 'H2', 'H3', 'H4', 'Blockquote']
                  }
                }}
              />
            </TextEditor>
          </FormGroup>
          
          <ButtonGroup>
            <SaveButton 
              type="button" 
              onClick={() => handleSave('draft')} 
              disabled={saving}
            >
              <FiSave size={18} /> {saving ? 'Saving...' : 'Save Draft'}
            </SaveButton>
            
            <SubmitButton 
              type="submit" 
              disabled={saving || !title || !imageUrl}
            >
              <FiSend size={18} /> {saving ? 'Submitting...' : 'Submit for Review'}
            </SubmitButton>
          </ButtonGroup>
        </EditorForm>
        
        <AIAssistant 
          articleData={{
            title,
            subtitle,
            section,
            content: editorState.getCurrentContent()
          }}
          onSuggestionApply={(type, content) => {
            // Handle applying AI suggestions to the article
            switch(type) {
              case 'headline':
                setTitle(content);
                break;
              case 'intro':
                // Would need to update editor state with the intro
                break;
              default:
                // Handle other suggestion types
            }
          }}
        />
      </div>
    </EditorContainer>
  );
};

export default ArticleEditor;